import { ActiveFocusArea, useFocusAreasHooks } from '@api/focusAreasApiHandler';
import { useUserActionsHooks } from '@api/userActionsApiHandler';
import LightBulbIcon from '@assets/images/icons/lightbulb-outlined.svg';
import { useAppSelector } from '@hooks/useAppSelector';
import useWindowDimensions from '@hooks/useWindowDimensions';
import { Alert } from '@mui/material';
import { CustomDialog, MotionImage, MotionText, showToast } from '@shared/atoms';
import { CurrentPrioritiesCard, RecommendedAreasCard, SystemButton } from '@shared/molecules';
import {
  CurrentPrioritiesSkeleton,
  convertActiveFocusArea,
  convertRecommendedFocusArea,
} from '@shared/organisms/Assessments/FocusAreas/FocusAreas';
import { useQueryClient } from '@tanstack/react-query';
import { ToastMessages } from 'constants/toastMessages';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import styles from './styles.module.scss';

interface InsightsSectionProps {
  activeError: boolean | null | Error;
  isLoadingActive: boolean;
  selectedPriorities: ActiveFocusArea[];
  refetchActivePriorities: () => void;
  isIndicatorEnabled: boolean;
}

export const CurrentPrioritiesSection: React.FC<InsightsSectionProps> = ({
  activeError,
  isLoadingActive,
  selectedPriorities,
  refetchActivePriorities,
  isIndicatorEnabled,
}) => {
  const history = useHistory();
  const { isDesktop } = useWindowDimensions();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const questionnaireId = params.get('questionnaireId');
  const [editOpen, setEditOpen] = useState(false);
  const [selectedAreasInEdit, setSelectedAreasInEdit] = useState<any[]>([]);
  const [removedPriorities, setRemovedPriorities] = useState<any[]>([]);
  const { chartSelectedAssessment } = useAppSelector((state) => state.assessmentScores || {});
  const queryClient = useQueryClient();

  const activePriorities = useMemo(() => {
    if (!selectedPriorities) return [];

    return selectedPriorities.map(convertActiveFocusArea);
  }, [selectedPriorities]);

  const addFocusAreasMutation = useFocusAreasHooks.useAddFocusAreas();
  const removeFocusAreaMutation = useFocusAreasHooks.useRemoveFocusArea();
  const addActionsToUserActionMutation = useUserActionsHooks.useAddActionsToUserAction();
  const [isSaving, setIsSaving] = useState(false);
  const batchRemoveFocusAreaMutation = useFocusAreasHooks.useBatchRemoveFocusArea();

  const {
    data: recommendedFocusAreasResponse,
    isLoading: isLoadingRecommended,
    error: recommendedError,
    refetch: refetchRecommended,
  } = useFocusAreasHooks.useRecommendedFocusAreas(chartSelectedAssessment?.assessmentId || 0);

  const recommendedPriorities = useMemo(() => {
    if (recommendedFocusAreasResponse?.success) {
      const converted = recommendedFocusAreasResponse.responseObject.map(
        convertRecommendedFocusArea,
      );

      return converted;
    }

    return [];
  }, [recommendedFocusAreasResponse?.responseObject, chartSelectedAssessment]);

  const handleRemoveCurrentPriority = async (priorityId: number) => {
    await removeFocusAreaMutation.mutateAsync(priorityId);
  };

  useEffect(() => {
    setSelectedAreasInEdit(activePriorities);
  }, [activePriorities, editOpen]);

  const handleAddPriority = async (priority: any) => {
    if (selectedAreasInEdit.find((p) => p.id === priority.id)) return;

    if (selectedAreasInEdit.length >= 10) {
      showToast(ToastMessages.priorities.error.MAXIMUM_10_PRIORITIES, undefined, 'error');

      return;
    }

    try {
      if (chartSelectedAssessment) {
        setSelectedAreasInEdit([...selectedAreasInEdit, priority]);
      } else {
        showToast(ToastMessages.priorities.error.CREATE_ASSESSMENT_FIRST, undefined, 'error');
      }
    } catch (error) {
      showToast(ToastMessages.common.UNEXPECTED_ERROR_TRY_AGAIN, undefined, 'error');
    }
  };

  const handleRemovePriority = async (priorityId: number) => {
    const priority = selectedAreasInEdit.find((p) => p.questionV2Id === priorityId);

    if (priority?.focusAreaId) {
      setSelectedAreasInEdit(selectedAreasInEdit.filter((p) => p.questionV2Id !== priorityId));
      setRemovedPriorities((prev) => {
        const exists = prev.some((p) => p.id === priorityId);

        return exists ? prev : [...prev, priority];
      });
    } else {
      setSelectedAreasInEdit(selectedAreasInEdit.filter((p) => p.questionV2Id !== priorityId));
    }
  };

  const isPrioritySelectedToEdit = useMemo(
    () => (priorityId: number) => selectedAreasInEdit.some((p) => p.questionV2Id === priorityId),
    [selectedAreasInEdit],
  );

  const handleContinue = async (priorities: ActiveFocusArea[]) => {
    // Prevent multiple simultaneous calls
    if (isSaving) return;

    const selectedNewIds = selectedAreasInEdit.map((dt) => dt.questionV2Id);
    const existingIds = selectedPriorities.map((dt) => dt.questionV2Id);
    const newPriorityIdsOnly = selectedNewIds.filter((id) => !existingIds.includes(id));

    setIsSaving(true);
    const questionnaireV2Id = Number(questionnaireId);

    try {
      // Remove priorities if any exist
      if (removedPriorities.length > 0) {
        const removePromises = removedPriorities
          .filter((priority) => priority.focusAreaId)
          .map((priority) => batchRemoveFocusAreaMutation.mutateAsync(priority.focusAreaId));

        if (removePromises.length > 0) {
          try {
            await Promise.all(removePromises);
            await queryClient.invalidateQueries({ queryKey: ['active-focus-areas'] });

            setRemovedPriorities([]);
          } catch (error) {
            showToast(
              ToastMessages.priorities.error.ERROR_OCCURRED_WHEN_REMOVING,
              undefined,
              'error',
            );
          }
        }
      }

      if (priorities?.length > 0) {
        const focusAreas = priorities.map((priority) => ({
          questionV2Id: priority.questionV2Id,
          concernPercentage: priority.concernPercentage || 0,
          priorityScore: priority.priorityScore || 0,
        }));

        const response = await addFocusAreasMutation.mutateAsync({
          userAssessmentId: chartSelectedAssessment?.assessmentId || 0,
          focusAreas,
        });

        if (response?.success) {
          showToast(
            ToastMessages.priorities.success.PRIORITIES_SAVED_SUCCESSFULLY,
            undefined,
            'success',
          );

          // Add actions to user action
          if (questionnaireId && newPriorityIdsOnly.length > 0) {
            await addActionsToUserActionMutation.mutateAsync({
              questionnaireId: questionnaireV2Id,
              focusAreas: newPriorityIdsOnly || [],
              indicators: null,
            });
          }
        } else if (response?.message === 'All the focus areas are already added') {
          showToast(ToastMessages.priorities.info.ALL_THE_AREAS_ALREADY_ADDED, undefined, 'info');
        } else {
          showToast(
            response?.message || ToastMessages.common.SOMETHING_WENT_MISSING_TRY_AGAIN,
            undefined,
            'error',
          );
        }
      }

      // Clean up state
      setRemovedPriorities([]);
      setEditOpen(false);
      await refetchActivePriorities();
    } catch (error) {
      showToast(ToastMessages.common.FAILED_TO_SAVE_CHANGES, undefined, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={styles.prioritiesContainer}>
      <div className={styles.card}>
        <div className={styles.topContainer}>
          <MotionText
            id="priorities-heading"
            as="h3"
            text="Your current priorities"
            className={styles.sectionTitle}
          />

          {!isIndicatorEnabled && (
            <SystemButton
              variant="outlinedPrimarySkyBlue"
              onClick={() => setEditOpen(true)}
              size="medium"
              className={styles.editCurrentPriorities}
            >
              Edit
            </SystemButton>
          )}
        </div>

        <MotionText
          id="priorities-heading"
          as="text"
          text="These areas are used to create your personalised action plan."
          className={styles.sectionDescription}
        />

        {activeError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load active focus areas. Please try refreshing.
          </Alert>
        )}

        {isLoadingActive ? (
          <CurrentPrioritiesSkeleton />
        ) : (
          <div className={styles.currentPrioritiesGrid}>
            {activePriorities.length > 0 ? (
              activePriorities.map((priority) => (
                <CurrentPrioritiesCard
                  key={priority.id}
                  priority={priority}
                  handleRemovePriority={() => handleRemoveCurrentPriority(priority?.focusAreaId)}
                />
              ))
            ) : (
              <div className={styles.emptyPrioritiesContainer}>
                <div className={styles.emptyContainer}>
                  <MotionImage
                    src={LightBulbIcon}
                    alt=""
                    aria-hidden="true"
                    className={styles.lightBulbIcon}
                  />
                  <div className={styles.emptyPrioritiesText}>
                    <MotionText as="h4" text="You haven't selected any priorities yet" />

                    <MotionText
                      as="text"
                      text="Selecting priorities helps us create a personalised action plan focused on
                      what matters most to you."
                      className={styles.text}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {editOpen && (
        <CustomDialog
          open={editOpen}
          onClose={() => {
            setEditOpen(false);
            setRemovedPriorities([]);
            setSelectedAreasInEdit(activePriorities);
          }}
          dialogStyles={{
            backgroundColor: '#f8f9fa',
            width: isDesktop ? 'auto' : '90vw',
            maxWidth: '95vw',
            height: isDesktop ? 'auto' : '90vh',
            maxHeight: '95vh',
            overflowY: 'auto',
            borderRadius: '8px',
            padding: '1rem',
            boxSizing: 'border-box',
          }}
          showMobileBorderRadius={false}
          showCloseButton
          aria-labelledby="wellbeing-hub-title"
        >
          <div className={styles.editPrioritiesSection}>
            <MotionText
              as="h3"
              text="Recommended priorities for you"
              className={styles.sectionTitle}
            />

            <MotionText
              as="text"
              text=" We recommend these priorities for you."
              className={styles.sectionDescription}
            />
            <div className={styles.recommendedPriorityGrid}>
              {recommendedPriorities?.length > 0 &&
                recommendedPriorities.map((priority) => {
                  const isSelected = isPrioritySelectedToEdit(priority.questionV2Id);

                  return (
                    <RecommendedAreasCard
                      key={priority.id}
                      priority={priority}
                      isSelected={isSelected}
                      handleRemovePriority={handleRemovePriority}
                      handleAddPriority={handleAddPriority}
                    />
                  );
                })}
            </div>

            <div className={styles.editSaveButton}>
              <SystemButton onClick={() => handleContinue(selectedAreasInEdit)} disabled={isSaving}>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                {isSaving ? 'Saving...' : 'Save'}
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
              </SystemButton>
            </div>
          </div>
        </CustomDialog>
      )}
    </div>
  );
};
